import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { RefObject, useCallback, useRef } from 'react';

const PX_PER_MM = 3.77952755906;

export enum Resolution {
  LOW = 1,
  NORMAL = 2,
  MEDIUM = 3,
  HIGH = 7,
  EXTREME = 12,
}

export enum Margin {
  NONE = 0,
  SMALL = 5,
  MEDIUM = 10,
  LARGE = 25,
}

interface GeneratePDFOptions {
  filename?: string;
  method?: 'save' | 'open' | 'build';
  resolution?: Resolution | number;
  page?: {
    margin?: Margin | number;
    format?: string;
    orientation?: 'portrait' | 'landscape';
  };
}

/**
 * Core PDF generation logic.
 * Fixes jsPDF "addImage does not support files of type 'UNKNOWN'" error
 * by slicing the canvas into page-sized JPEG chunks with explicit format.
 */
async function generatePDFFromElement(element: HTMLElement, options: GeneratePDFOptions = {}) {
  const scale = options.resolution ?? Resolution.MEDIUM;
  const margin = options.page?.margin ?? Margin.NONE;
  const pageFormat = options.page?.format ?? 'a4';
  const orientation = options.page?.orientation ?? 'portrait';
  const filename = options.filename ?? 'document.pdf';

  const canvas = await html2canvas(element, {
    useCORS: true,
    logging: false,
    scale,
  });

  const pdf = new jsPDF({
    format: pageFormat,
    orientation,
    unit: 'mm',
  });

  const marginMM = typeof margin === 'number' ? margin : 0;
  const pageWidthMM = pdf.internal.pageSize.getWidth() - 2 * marginMM;
  const pageHeightMM = pdf.internal.pageSize.getHeight() - 2 * marginMM;
  const pageWidthPx = pageWidthMM * PX_PER_MM;
  const pageHeightPx = pageHeightMM * PX_PER_MM;

  // Horizontal fit factor (shrink if canvas is wider than page)
  const origWidth = canvas.width / scale;
  const hFit = origWidth > pageWidthPx ? origWidth / pageWidthPx : 1;

  // Available page height in canvas pixels (integer for clean slicing)
  const canvasPageHeight = Math.floor(pageHeightPx * scale * hFit);
  const numPages = Math.ceil(canvas.height / canvasPageHeight);
  const factor = scale * PX_PER_MM * hFit;

  for (let page = 1; page <= numPages; page++) {
    if (page > 1) pdf.addPage(pageFormat, orientation);

    const offsetY = canvasPageHeight * (page - 1);
    const sliceHeight = Math.min(canvas.height - offsetY, canvasPageHeight);
    if (sliceHeight <= 0) continue;

    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = sliceHeight;
    const ctx = pageCanvas.getContext('2d');
    if (!ctx) continue;
    ctx.drawImage(canvas, 0, offsetY, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

    // Use JPEG (smaller than PNG) with positional args for explicit format
    const imgData = pageCanvas.toDataURL('image/jpeg', 0.95);
    const imgWidthMM = pageCanvas.width / factor;
    const imgHeightMM = pageCanvas.height / factor;

    pdf.setPage(page);
    pdf.addImage(imgData, 'JPEG', marginMM, marginMM, imgWidthMM, imgHeightMM);
  }

  if (options.method === 'build') return pdf;
  if (options.method === 'open') {
    window.open(pdf.output('bloburl') as unknown as string, '_blank');
    return pdf;
  }

  await pdf.save(filename, { returnPromise: true });
  return pdf;
}

/**
 * Standalone function to generate PDF from a ref.
 * Drop-in replacement for react-to-pdf's default export.
 */
export async function generatePDF(targetRef: RefObject<HTMLElement>, options?: GeneratePDFOptions) {
  const element = targetRef.current;
  if (!element) {
    console.error('Unable to get the target element.');
    return;
  }
  return generatePDFFromElement(element, options);
}

interface UseGeneratePDFOptions {
  filename: string;
}

/**
 * Custom hook replacement for react-to-pdf's usePDF.
 */
export function useGeneratePDF({ filename }: UseGeneratePDFOptions) {
  const targetRef = useRef<HTMLDivElement>(null);

  const toPDF = useCallback(async () => {
    const element = targetRef.current;
    if (!element) {
      console.error('Unable to get the target element.');
      return;
    }
    return generatePDFFromElement(element, { filename });
  }, [filename]);

  return { toPDF, targetRef };
}
